import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { ScriptRow, ScriptLabel, ScriptPre, CopyBtn } from './ispWizardUI';
import { deleteIspServer, getIspServerScripts } from '@/api/isp';

/**
 * Remove a MikroTik from SmartFi.
 *
 * A modal, never `window.confirm` — this dashboard does not use native browser
 * dialogs, and a one-line "Are you sure?" could not carry the thing the operator
 * actually needs to know.
 *
 * 🔴 WHAT IT DOES NOT DO IS THE POINT. Deleting the record does not reach into
 * the router. The WireGuard interface, the `smartfi-isp` account and the
 * firewall rule stay on a MikroTik that is still carrying paying subscribers.
 * On the piso-wifi side that same silence left routers stranded with config and
 * no peer, recoverable only over the owner port or a Netinstall. So this names
 * exactly what stays and hands over the script that removes it — before the
 * operator confirms, not after they have lost the tunnel they would have needed.
 */
interface Props {
   server: { id: string; name: string };
   onCancel: () => void;
   onDeleted: () => void;
}

const IspServerDelete: React.FC<Props> = ({ server, onCancel, onDeleted }) => {
   const [cleanup, setCleanup] = useState('');
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState('');

   useEffect(() => {
      getIspServerScripts(server.id)
         .then((r) => setCleanup(r.cleanup))
         .catch(() => setCleanup(''));
   }, [server.id]);

   const copy = async () => {
      try {
         await navigator.clipboard.writeText(cleanup);
         toast.success('Cleanup script copied.');
      } catch {
         toast.error('Could not copy — select the script and copy by hand.');
      }
   };

   const remove = async () => {
      setError('');
      setBusy(true);
      try {
         await deleteIspServer(server.id);
         toast.success(`${server.name} removed.`);
         onDeleted();
      } catch (e: unknown) {
         // A 409 here is the "subscriber lines still point at it" refusal, and
         // its message names the count. Showing it verbatim is the whole value.
         setError(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not remove it. Try again.'
         );
      } finally {
         setBusy(false);
      }
   };

   return (
      <Wrapper>
         <Lead>
            Remove <strong>{server.name}</strong> from SmartFi? Its tunnel address
            is released and SmartFi stops reading it.
         </Lead>

         <Callout>
            <CalloutTitle>Your router keeps running</CalloutTitle>
            <CalloutBody>
               Nothing is changed on the MikroTik itself — your PPPoE server,
               pools, profiles and secrets are untouched, and your subscribers
               stay online. But SmartFi&apos;s own leftovers stay too: the{' '}
               <Mono>wg-smartfi</Mono> tunnel, the <Mono>smartfi-isp</Mono>{' '}
               account and one firewall rule. Paste the script below in Winbox to
               clear them out.
            </CalloutBody>
         </Callout>

         {cleanup && (
            <>
               <ScriptRow>
                  <ScriptLabel>
                     Optional — paste in Winbox → New Terminal, before or after
                     removing it here.
                  </ScriptLabel>
                  <CopyBtn type="button" onClick={copy}>
                     Copy cleanup
                  </CopyBtn>
               </ScriptRow>
               <ScriptPre>{cleanup}</ScriptPre>
            </>
         )}

         {error && <Err>{error}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel} disabled={busy}>
               Cancel
            </Ghost>
            <Danger type="button" onClick={remove} disabled={busy}>
               {busy ? 'Removing…' : 'Remove this server'}
            </Danger>
         </Actions>
      </Wrapper>
   );
};

export default IspServerDelete;

const Wrapper = styled.div`
   width: 560px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const Lead = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.MAIN_DARK};
`;

/**
 * Amber, not red.
 *
 * Nothing is broken and nothing is lost — this is a thing to do next, and a red
 * panel here would read as a warning that the delete is dangerous to the
 * operator's subscribers. It is not; that is what the panel says.
 */
const Callout = styled.div`
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 5px;
`;

const CalloutTitle = styled.div`
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const CalloutBody = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.65;
   color: ${colors.GRAY5};
`;

const Mono = styled.code`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11px;
   padding: 1px 5px;
   border-radius: 4px;
   background: ${colors.GRAY14};
   color: ${colors.MAIN_DARK};
`;

const Err = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 2px;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

const Danger = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.DANGER};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;
